import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, authFetch } from '../../context/AuthContext';
import { Permissions } from '../../types';

const PERM_LABELS: { key: keyof Permissions; label: string; desc: string; adminOnly?: boolean }[] = [
  { key: 'production_advance', label: '생산 단계 진행',  desc: '생산 주문을 다음 단계로 진행',  adminOnly: true },
  { key: 'production_delay',   label: '생산 지연 처리',  desc: '생산 주문을 지연 상태로 변경',   adminOnly: true },
  { key: 'production_create',  label: '생산 주문 등록',  desc: '새 생산 주문 생성' },
  { key: 'inventory_in',       label: '재고 입고',        desc: '재고 입고 처리' },
  { key: 'inventory_out',      label: '재고 출고',        desc: '재고 출고 처리' },
  { key: 'report_view',        label: '리포트 조회',      desc: '통계 리포트 열람' },
];

interface Partner { id: number; code: string; name: string; role: string; }
interface User {
  id: number; name: string; email: string; role: string;
  is_active: number; last_login: string | null; created_at: string;
  partner_id: number; partner_name: string; partner_code: string;
}

const ROLE_LABEL: Record<string, string> = { admin: '관리자', partner: '파트너', viewer: '조회' };
const ROLE_COLOR: Record<string, string> = {
  admin:   'bg-red-100 text-red-700',
  partner: 'bg-amber-100 text-amber-700',
  viewer:  'bg-stone-100 text-stone-600',
};

const RoleBadge   = ({ role }: { role: string }) => (
  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${ROLE_COLOR[role] ?? ROLE_COLOR.viewer}`}>
    {ROLE_LABEL[role] ?? role}
  </span>
);
const StatusBadge = ({ active }: { active: number }) => active
  ? <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-green-100 text-green-700">활성</span>
  : <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-stone-100 text-stone-500">비활성</span>;

const Modal = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
      <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
        <h2 className="font-bold text-stone-900">{title}</h2>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-700 text-2xl leading-none">×</button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

const Input = ({ label, required, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) => (
  <div>
    <label className="block text-xs font-semibold text-stone-500 mb-1">{label}{required && ' *'}</label>
    <input
      {...props}
      required={required}
      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-stone-50"
    />
  </div>
);

const Select = ({ label, children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string }) => (
  <div>
    <label className="block text-xs font-semibold text-stone-500 mb-1">{label}</label>
    <select {...props}
      className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-stone-50">
      {children}
    </select>
  </div>
);

type ModalType = 'add' | 'edit' | 'password' | 'delete' | 'perms' | null;

export default function UsersPage() {
  const { user: me } = useAuth();

  const [users,    setUsers]    = useState<User[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState<ModalType>(null);
  const [target,   setTarget]   = useState<User | null>(null);
  const [saving,   setSaving]   = useState(false);
  const [errMsg,   setErrMsg]   = useState('');
  const [toast,    setToast]    = useState('');

  const [permsForm, setPermsForm] = useState<Permissions>({
    production_advance: false, production_delay: false, production_create: true,
    inventory_in: true, inventory_out: true, report_view: true,
  });

  const blank = { name:'', email:'', password:'', role:'partner', partner_id:'', is_active:'1' };
  const [form,   setForm]   = useState(blank);
  const [pwForm, setPwForm] = useState({ password:'', confirm:'' });

  /* ── 데이터 로드 ── */
  const loadUsers = useCallback(async () => {
    setLoading(true);
    const [ur, pr] = await Promise.all([
      authFetch('/api/users'),
      authFetch('/api/users/partners'),
    ]);
    if (ur.ok) setUsers(await ur.json());
    if (pr.ok) setPartners(await pr.json());
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const flash = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3500); };
  const close  = () => { setModal(null); setTarget(null); setErrMsg(''); };

  const openPerms = async (u: User) => {
    setTarget(u);
    if (u.role === 'admin') {
      setPermsForm({ production_advance:true, production_delay:true, production_create:true, inventory_in:true, inventory_out:true, report_view:true });
    } else {
      const res = await authFetch(`/api/users/${u.id}/permissions`);
      if (res.ok) setPermsForm(await res.json());
    }
    setErrMsg('');
    setModal('perms');
  };

  const handlePerms = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setSaving(true);
    const { ok, data } = await apiCall(`/api/users/${target.id}/permissions`, 'PUT', permsForm);
    setSaving(false);
    if (!ok) { setErrMsg(data.error ?? '저장 실패'); return; }
    flash(`${target.name} 권한이 저장되었습니다.`);
    close();
  };

  const openAdd  = () => {
    setForm({ ...blank, partner_id: String(partners[0]?.id ?? '') });
    setErrMsg('');
    setModal('add');
  };
  const openEdit = (u: User) => {
    setTarget(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, partner_id: String(u.partner_id), is_active: String(u.is_active) });
    setErrMsg('');
    setModal('edit');
  };
  const openPw  = (u: User) => { setTarget(u); setPwForm({ password:'', confirm:'' }); setErrMsg(''); setModal('password'); };
  const openDel = (u: User) => { setTarget(u); setModal('delete'); };

  /* ── 공통 API 호출 래퍼 ── */
  const apiCall = async (url: string, method: string, body?: object): Promise<{ ok: boolean; data: any }> => {
    const res  = await authFetch(url, { method, body: body ? JSON.stringify(body) : undefined });
    const data = await res.json();
    return { ok: res.ok, data };
  };

  /* ── 추가 ── */
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password.length < 6) { setErrMsg('비밀번호는 6자 이상이어야 합니다.'); return; }
    setSaving(true); setErrMsg('');
    const { ok, data } = await apiCall('/api/users', 'POST', {
      name: form.name, email: form.email, password: form.password,
      role: form.role, partner_id: Number(form.partner_id),
    });
    setSaving(false);
    if (!ok) { setErrMsg(data.error ?? '추가 실패'); return; }
    await loadUsers();
    flash(`${data.name} 계정이 추가되었습니다.`);
    close();
  };

  /* ── 수정 ── */
  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setSaving(true); setErrMsg('');
    const { ok, data } = await apiCall(`/api/users/${target.id}`, 'PUT', {
      name: form.name, email: form.email,
      role: form.role, partner_id: Number(form.partner_id),
      is_active: Number(form.is_active),
    });
    setSaving(false);
    if (!ok) { setErrMsg(data.error ?? '수정 실패'); return; }
    await loadUsers();
    flash('사용자 정보가 수정되었습니다.');
    close();
  };

  /* ── 비밀번호 변경 ── */
  const handlePw = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwForm.password.length < 6) { setErrMsg('비밀번호는 6자 이상이어야 합니다.'); return; }
    if (pwForm.password !== pwForm.confirm) { setErrMsg('비밀번호가 일치하지 않습니다.'); return; }
    setSaving(true); setErrMsg('');
    const { ok, data } = await apiCall(`/api/users/${target!.id}/password`, 'PATCH', { password: pwForm.password });
    setSaving(false);
    if (!ok) { setErrMsg(data.error ?? '변경 실패'); return; }
    flash(`${target?.name} 비밀번호가 변경되었습니다.`);
    close();
  };

  /* ── 삭제 ── */
  const handleDelete = async () => {
    if (!target) return;
    setSaving(true);
    const { ok, data } = await apiCall(`/api/users/${target.id}`, 'DELETE');
    setSaving(false);
    if (!ok) { setErrMsg(data.error ?? '삭제 실패'); return; }
    await loadUsers();
    flash(data.note ?? `${target.name} 처리 완료`);
    close();
  };

  /* ── 활성/비활성 토글 ── */
  const toggleActive = async (u: User) => {
    const endpoint = u.is_active ? 'deactivate' : 'activate';
    const { ok } = await apiCall(`/api/users/${u.id}/${endpoint}`, 'PATCH');
    if (ok) { await loadUsers(); flash(`${u.name} 계정이 ${u.is_active ? '비활성화' : '활성화'}되었습니다.`); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-amber-500 text-sm animate-pulse">사용자 목록 불러오는 중...</div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* 헤더 */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">사용자 관리</h1>
          <p className="text-stone-500 text-sm mt-1">파트너사 로그인 계정 추가·수정·삭제</p>
        </div>
        <button onClick={openAdd}
          className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-2 rounded-lg text-sm transition flex items-center gap-2">
          <span className="text-lg leading-none">+</span> 사용자 추가
        </button>
      </div>

      {/* 토스트 */}
      {toast && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          {toast}
        </div>
      )}

      {/* 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: '전체 계정',   val: users.length,                            bg: 'bg-white' },
          { label: '활성 계정',   val: users.filter(u => u.is_active).length,   bg: 'bg-green-50' },
          { label: '비활성 계정', val: users.filter(u => !u.is_active).length,  bg: 'bg-stone-50' },
          { label: '관리자',      val: users.filter(u => u.role==='admin').length, bg: 'bg-red-50' },
        ].map(c => (
          <div key={c.label} className={`${c.bg} rounded-xl p-4 shadow-sm`}>
            <p className="text-2xl font-bold text-stone-800">{c.val}</p>
            <p className="text-xs text-stone-500 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      {/* 테이블 */}
      <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-100 text-xs font-semibold text-stone-500">
              <th className="text-left px-5 py-3">이름 / 이메일</th>
              <th className="text-left px-5 py-3">파트너사</th>
              <th className="text-center px-5 py-3">권한</th>
              <th className="text-center px-5 py-3">상태</th>
              <th className="text-left px-5 py-3">마지막 로그인</th>
              <th className="text-center px-5 py-3">관리</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className={`border-b border-stone-50 transition ${!u.is_active ? 'opacity-50' : 'hover:bg-stone-50'}`}>
                <td className="px-5 py-3.5">
                  <p className="font-semibold text-stone-800 text-sm">{u.name}</p>
                  <p className="text-xs text-stone-400 mt-0.5">{u.email}</p>
                </td>
                <td className="px-5 py-3.5">
                  <span className="text-xs font-bold bg-stone-100 text-stone-600 px-2 py-0.5 rounded mr-1.5">{u.partner_code}</span>
                  <span className="text-sm text-stone-700">{u.partner_name}</span>
                </td>
                <td className="px-5 py-3.5 text-center"><RoleBadge role={u.role} /></td>
                <td className="px-5 py-3.5 text-center"><StatusBadge active={u.is_active} /></td>
                <td className="px-5 py-3.5 text-sm text-stone-500">
                  {u.last_login ?? <span className="text-stone-300">없음</span>}
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center justify-center gap-1">

                    {/* 수정 */}
                    <button onClick={() => openEdit(u)} title="정보 수정"
                      className="p-1.5 rounded hover:bg-amber-50 text-stone-400 hover:text-amber-600 transition">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>

                    {/* 권한 설정 */}
                    <button onClick={() => openPerms(u)} title="세부 권한 설정"
                      className="p-1.5 rounded hover:bg-purple-50 text-stone-400 hover:text-purple-600 transition">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </button>

                    {/* 비밀번호 */}
                    <button onClick={() => openPw(u)} title="비밀번호 변경"
                      className="p-1.5 rounded hover:bg-blue-50 text-stone-400 hover:text-blue-600 transition">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </button>

                    {/* 활성/비활성 (본인 제외) */}
                    {u.id !== me?.id && (
                      <button onClick={() => toggleActive(u)}
                        title={u.is_active ? '비활성화' : '활성화'}
                        className={`p-1.5 rounded transition text-stone-400 ${u.is_active
                          ? 'hover:bg-orange-50 hover:text-orange-500'
                          : 'hover:bg-green-50 hover:text-green-600'}`}>
                        {u.is_active
                          ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                          : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        }
                      </button>
                    )}

                    {/* 삭제 (본인 제외) */}
                    {u.id !== me?.id && (
                      <button onClick={() => openDel(u)} title="삭제"
                        className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-500 transition">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={6} className="text-center py-16 text-stone-400">등록된 사용자가 없습니다.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── 모달: 추가 ── */}
      {modal === 'add' && (
        <Modal title="사용자 추가" onClose={close}>
          <form onSubmit={handleAdd} className="space-y-3">
            <Input label="이름" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="홍길동" />
            <Input label="이메일" required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="user@brand.com" />
            <Input label="비밀번호 (6자 이상)" required type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••" />
            <div className="grid grid-cols-2 gap-3">
              <Select label="파트너사" value={form.partner_id} onChange={e => setForm({...form, partner_id: e.target.value})}>
                {partners.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
              </Select>
              <Select label="권한" value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                <option value="admin">관리자</option>
                <option value="partner">파트너</option>
                <option value="viewer">조회</option>
              </Select>
            </div>
            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50 transition">취소</button>
              <button type="submit" disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm transition">
                {saving ? '처리 중...' : '추가'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── 모달: 수정 ── */}
      {modal === 'edit' && target && (
        <Modal title={`사용자 수정 — ${target.name}`} onClose={close}>
          <form onSubmit={handleEdit} className="space-y-3">
            <Input label="이름" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
            <Input label="이메일" required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
            <div className="grid grid-cols-2 gap-3">
              <Select label="파트너사" value={form.partner_id} onChange={e => setForm({...form, partner_id: e.target.value})}>
                {partners.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
              </Select>
              <Select label="권한" value={form.role}
                disabled={target.id === me?.id}
                onChange={e => setForm({...form, role: e.target.value})}>
                <option value="admin">관리자</option>
                <option value="partner">파트너</option>
                <option value="viewer">조회</option>
              </Select>
            </div>
            <Select label="계정 상태" value={form.is_active}
              disabled={target.id === me?.id}
              onChange={e => setForm({...form, is_active: e.target.value})}>
              <option value="1">활성</option>
              <option value="0">비활성</option>
            </Select>
            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50 transition">취소</button>
              <button type="submit" disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm transition">
                {saving ? '처리 중...' : '저장'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── 모달: 비밀번호 ── */}
      {modal === 'password' && target && (
        <Modal title={`비밀번호 변경 — ${target.name}`} onClose={close}>
          <form onSubmit={handlePw} className="space-y-3">
            <Input label="새 비밀번호 (6자 이상)" required type="password"
              value={pwForm.password} onChange={e => setPwForm({...pwForm, password: e.target.value})} placeholder="새 비밀번호" />
            <Input label="새 비밀번호 확인" required type="password"
              value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})} placeholder="비밀번호 재입력" />
            {pwForm.confirm && pwForm.password !== pwForm.confirm && (
              <p className="text-red-500 text-xs">비밀번호가 일치하지 않습니다.</p>
            )}
            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50 transition">취소</button>
              <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm transition">
                {saving ? '변경 중...' : '변경'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── 모달: 세부 권한 설정 ── */}
      {modal === 'perms' && target && (
        <Modal title={`세부 권한 설정 — ${target.name}`} onClose={close}>
          <form onSubmit={handlePerms}>
            {target.role === 'admin' && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-lg">
                관리자는 모든 권한이 자동으로 부여됩니다.
              </div>
            )}
            <div className="space-y-2 mb-5">
              {PERM_LABELS.map(p => (
                <label key={p.key}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                    permsForm[p.key]
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-stone-200 bg-white hover:bg-stone-50'
                  } ${target.role === 'admin' ? 'opacity-60 pointer-events-none' : ''}`}>
                  <input
                    type="checkbox"
                    className="mt-0.5 w-4 h-4 accent-amber-500"
                    checked={target.role === 'admin' ? true : permsForm[p.key]}
                    disabled={target.role === 'admin'}
                    onChange={e => setPermsForm(prev => ({...prev, [p.key]: e.target.checked}))}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-stone-700">{p.label}</span>
                      {p.adminOnly && (
                        <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">관리자 권장</span>
                      )}
                    </div>
                    <p className="text-xs text-stone-400 mt-0.5">{p.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg mb-3">{errMsg}</p>}
            <div className="flex gap-3">
              <button type="button" onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50 transition">취소</button>
              <button type="submit" disabled={saving || target.role === 'admin'}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm transition">
                {saving ? '저장 중...' : '권한 저장'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── 모달: 삭제 ── */}
      {modal === 'delete' && target && (
        <Modal title="계정 삭제" onClose={close}>
          <div className="text-center py-2">
            <div className="text-5xl mb-4">오류</div>
            <p className="font-semibold text-stone-800 mb-1">{target.name} 계정을 삭제하시겠습니까?</p>
            <p className="text-stone-500 text-sm mb-6">이력이 있으면 비활성화로 처리됩니다.</p>
            {errMsg && <p className="text-red-500 text-xs mb-3">{errMsg}</p>}
            <div className="flex gap-3">
              <button onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50 transition">취소</button>
              <button onClick={handleDelete} disabled={saving}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm transition">
                {saving ? '처리 중...' : '삭제'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
